import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Auth } from '@freelancer/auth';
import { ArrowPostJobFormState } from '@freelancer/datastore/post-job-page-form-state';
import { ProjectViewProject } from '@freelancer/datastore/project-view-projects';
import * as djv from 'djv';
import * as Rx from 'rxjs';
import {
  distinctUntilChanged,
  map,
  publishReplay,
  refCount,
  switchMap,
} from 'rxjs/operators';
import { definitions, topLevelSchemas } from './json-schemas';

/**
 * LOCAL STORAGE API:
 *
 * Use Cases of `undefined` and `null`:
 * When an observable for an item is created, if the item does not exist yet,
 * then `undefined` is initially emitted, indicating 'no value set yet'.
 * Since items in localStorage must be JSON serialisable, we can use `null` to indicate
 * when an item has been removed from localStorage.
 * These items will still have meta-information and are treated like any other item.
 *
 */

/**
 *  Holds the types which can be stored in local storage i.e. are JSON serialisable.
 *  Values can be restricted to solely their type,
 *  or can also be allowed to be `null` (if the item needs to be able to be deleted).
 */
export interface LocalStorageTypes {
  /* FLN LOCAL STORAGE TYPES */
  readonly arrowPostProjectDraft: ArrowPostJobFormState | null; // We can stop using this once draft support is added
  readonly postProjectObject: ProjectViewProject | null; // We can stop using this once login/signup is in webapp
  readonly webappThreadListMinimise: boolean;

  /* TYPES USED FOR TESTING ONLY */
  readonly testProperty: number;
  readonly testName: string | null;
  readonly testProfile: MockProfile;
}

interface MockProfile {
  id: number;
  name: string;
}

/**
 * The meta-object for storing into local storage.
 */
export interface LocalStorageMetaObject<T extends keyof LocalStorageTypes> {
  readonly timeCreated: number;
  readonly timeUpdated: number;
  readonly lastReadTime: number;
  // TTL?: number;
  readonly item: LocalStorageTypes[T];
}

/**
 * A map of the local storage objects indexed by the authenticated user who created them.
 */
export interface LocalStorageEntry<T extends keyof LocalStorageTypes> {
  [authId: string]: LocalStorageMetaObject<T>;
}

@Injectable()
export class LocalStorage {
  private subjects: {
    [key: string]: {
      [authId: string]: Rx.BehaviorSubject<
        LocalStorageTypes[keyof LocalStorageTypes] | undefined
      >;
    };
  };
  authId$: Rx.Observable<string>;
  authId: string;
  private readonly env = djv();
  private readonly LOGGED_OUT_KEY = 'logged-out';
  private isInitialized = false;

  constructor(
    private auth: Auth,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  /*
   * Initialisation only occurs when the functions are being called,
   * allowing for no side effects during injection
   * http://misko.hevery.com/code-reviewers-guide/flaw-constructor-does-real-work/
   */
  private init(): void {
    this.subjects = {};

    this.authId$ = toObservable(this.auth.getUserId()).pipe(
      map(id => {
        if (id === undefined) {
          return this.LOGGED_OUT_KEY;
        }
        return `id-${id}`;
      }),
      publishReplay(1),
      refCount(),
    );

    this.authId$.subscribe(id => {
      this.authId = id;
    });

    this.env.addSchema('defs.json', definitions);

    /*
      This event will only be fired in tabs other than the current one.
      See https://www.w3.org/TR/webstorage/#the-localstorage-attribute

      If other tabs have an observable of the changed item, the observable will emit the change.
    */

    window.addEventListener(
      'storage',
      <T extends keyof LocalStorageTypes>(e: StorageEvent): void => {
        if (!e.key) {
          return;
        }

        if (this.subjects[e.key] && this.subjects[e.key][this.authId]) {
          let newObject: LocalStorageEntry<T>;
          try {
            newObject = JSON.parse(e.newValue || '{}');
          } catch (err) {
            return;
          }

          this.subjects[e.key][this.authId].next(
            newObject[this.authId] && newObject[this.authId].item,
          );
        }
      },
    );

    this.isInitialized = true;
  }

  /**
   * Returns the current item under `key` for the current authId
   * or under the `loggedOutKey` if `loggedOutMode` is true.
   * (Non-observable version)
   */
  get<T extends keyof LocalStorageTypes>(
    key: T,
    loggedOutMode?: boolean,
  ): LocalStorageTypes[T] | undefined {
    if (!isPlatformBrowser(this.platformId)) {
      return undefined;
    }

    if (!this.isInitialized) {
      this.init();
    }

    const authKey = loggedOutMode ? this.LOGGED_OUT_KEY : this.authId;
    const entryObj = getEntryObject(key, authKey);

    if (!entryObj || !entryObj[authKey]) {
      // if parsing error occurred, or the key did not exist yet.
      return undefined;
    }

    const { item } = entryObj[authKey];

    // DJV CHECK
    if (!this.isValidItem(key, item)) {
      return undefined;
    }
    return item;
  }

  /**
   * Returns an OBSERVABLE of the current item under `key` for the current authId
   * or under the `loggedOutKey` if `loggedOutMode` is true.
   */
  getObservable<T extends keyof LocalStorageTypes>(
    key: T,
    loggedOutMode?: boolean,
  ): Rx.Observable<LocalStorageTypes[T] | undefined> {
    if (!isPlatformBrowser(this.platformId)) {
      return Rx.of(undefined);
    }

    if (!this.isInitialized) {
      this.init();
    }

    return this.authId$.pipe(
      switchMap(authId => {
        const authKey = loggedOutMode ? this.LOGGED_OUT_KEY : authId;
        const entryObj = getEntryObject(key, authKey);

        if (!entryObj) {
          // parsing error occured
          return Rx.of(undefined);
        }

        // item obtained does not conform to allowed LocalStorageTypes
        if (entryObj[authId] && !this.isValidItem(key, entryObj[authId].item)) {
          return Rx.of(undefined);
        }

        if (!this.subjects[key]) {
          this.subjects[key] = {};
        }
        if (!this.subjects[key][authKey]) {
          // CREATE SUBJECT
          this.subjects[key][authKey] = new Rx.BehaviorSubject(
            entryObj[authKey] ? entryObj[authKey].item : undefined, // if object has never been set in local storage, emit undefined.
          );
        }

        return this.subjects[key][authKey].asObservable().pipe(
          distinctUntilChanged(), // we only want an emission if the local storage entry has actually changed
        );
      }),
    );
  }

  /**
   * Stores `item` in local storage for `key` under the current authId
   * or under the `loggedOutKey` if `loggedOutMode` is true.
   * If there is a subject for that particular entry, notify the observers (of that subject) of the change.
   */
  set<T extends keyof LocalStorageTypes>(
    key: T,
    item: LocalStorageTypes[T],
    loggedOutMode?: boolean,
  ): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.isInitialized) {
      this.init();
    }
    const authKey = loggedOutMode ? this.LOGGED_OUT_KEY : this.authId;
    setRaw(key, authKey, item);
    if (this.subjects[key] && this.subjects[key][authKey]) {
      this.subjects[key][authKey].next(item);
    }
  }

  /**
   * Checks whether the item is valid against the JSON Schema.
   */
  private isValidItem<T extends keyof LocalStorageTypes>(
    key: T,
    item: any,
  ): boolean {
    this.env.addSchema(key, topLevelSchemas[key]);
    if (this.env.validate(key, item)) {
      console.warn(
        "Failed to load item from local storage as its format has changed. Check that it still validates against the schema at 'local-storage/json-schemas.ts'",
      );
      return false;
    }
    return true;
  }
}

/* HELPER FUNCTIONS */
/**
 * Directly store a @param item for a given @param authId.
 */
function setRaw<T extends keyof LocalStorageTypes>(
  key: T,
  authId: string,
  item: LocalStorageTypes[T],
): void {
  const entryObj = getEntryObject(key, authId);

  if (!entryObj) {
    // parsing error occurred.
    return;
  }

  const currentTime = Date.now();

  const addItem = entryObj[authId]
    ? {
        ...entryObj[authId],
        timeUpdated: currentTime,
        item,
      }
    : {
        timeUpdated: currentTime,
        timeCreated: currentTime,
        lastReadTime: currentTime,
        item,
      };

  entryObj[authId] = addItem;

  // if doesn't exist, creates, otherwise overrides.
  window.localStorage.setItem(key, JSON.stringify(entryObj));
}

/**
 * Turns the string when you first index into local storage (via `key`)
 * into a JSON object and updates `lastReadTime` for the particular `authId`.
 */
function getEntryObject<T extends keyof LocalStorageTypes>(
  key: T,
  authId: string,
): LocalStorageEntry<T> | null {
  let entryObj: LocalStorageEntry<T>;

  try {
    entryObj = JSON.parse(window.localStorage.getItem(key) || '{}');
  } catch (e) {
    return null;
  }

  // update `lastReadTime`
  if (entryObj[authId]) {
    entryObj[authId] = {
      ...entryObj[authId],
      lastReadTime: Date.now(),
    };
    window.localStorage.setItem(key, JSON.stringify(entryObj));
  }

  return entryObj;
}

function toObservable<T>(x$: T | Rx.Observable<T>): Rx.Observable<T> {
  return Rx.isObservable(x$) ? x$ : Rx.of(x$);
}
